import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { useDarkMode } from "../../components/Theme/useDarkMode";
import { fmtMoney } from "../OrgOverview/format";
import ChatPanel from "./ChatPanel";
import { buildIncomeTree, INCOME_TYPES } from "./incomeTree";
import { fetchIncomeRunDetail } from "./api";
import "./IncomeCalculationRuns.css";

const useQueryEnv = () => {
  const { search } = useLocation();
  return useMemo(() => {
    const env = new URLSearchParams(search).get("env");
    return env === "prod" ? "prod" : env === "dev" ? "dev" : null;
  }, [search]);
};

const STATUS_COPY = {
  qualifying: "Qualifying",
  conditional: "Needs documents",
  excluded: "Excluded",
  resolved: "Verified",
  linked: "Linked",
  "missing-evidence": "Missing evidence",
  "manual-review": "Review needed",
};

const METHOD_COPY = {
  baseline: "W-2 / paystub selected",
  "voe-only": "VOE-only selected",
  combined: "Combined evidence",
  zero: "No documented income",
  "manual-review": "Manual review",
};

// Self-employment income types come off the business track in PascalCase.
const INCOME_TYPE_COPY = {
  SelfEmployment: "Self-employment",
  SCorporation: "S corporation",
};

function incomeTypeLabel(type) {
  return INCOME_TYPE_COPY[type] || type.replace(/([a-z])([A-Z])/g, "$1 $2");
}

const CALCULATION_TRACK_COPY = {
  "schedule-c-tax-return": "Sole proprietorship · Schedule C",
  "s-corporation-form-91": "S corporation · Form 91 Section III",
};

function calculationTrackLabel(track) {
  return CALCULATION_TRACK_COPY[track] || track || "Self-employment";
}

const StatusPill = ({ status }) => (
  <span className={`icr-pill icr-pill--${status || "queued"}`}>
    {STATUS_COPY[status] || (status || "queued").replace("-", " ")}
  </span>
);

function formatValue(value, valueType) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  if (valueType === "money" && Number.isFinite(Number(value))) {
    return fmtMoney(Number(value));
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "number") {
    return value.toLocaleString("en-US");
  }

  return String(value);
}

function formatFormulaInput(input) {
  const countLike =
    /period|month|year|hour|day|count/i.test(input.label) &&
    !/amount|income|earnings|wages|rate/i.test(input.label);

  return countLike
    ? input.value.toLocaleString("en-US")
    : fmtMoney(input.value);
}

function formatMonthly(value) {
  return typeof value === "number" ? `${fmtMoney(value)} / month` : "—";
}

function formatDelta(value) {
  if (typeof value !== "number") {
    return "—";
  }
  const prefix = value > 0 ? "+" : "";

  return `${prefix}${fmtMoney(value)} / month`;
}

function methodLabel(method) {
  return METHOD_COPY[method] || String(method || "Final decision");
}

function FieldRows({ fields }) {
  if (!fields?.length) {
    return <div className="icr-doc__empty">No fields were extracted.</div>;
  }

  return (
    <div className="icr-field-table">
      {fields.map((field) => (
        <div className="icr-field-row" key={field.key}>
          <div className="icr-field-row__name">
            <span>{field.label}</span>
            {field.explanation && (
              <details className="icr-field-row__audit">
                <summary>How this was verified</summary>
                <p>{field.explanation}</p>
              </details>
            )}
          </div>
          <strong className="icr-field-row__value">
            {formatValue(field.value, field.valueType)}
          </strong>
          <StatusPill status={field.status} />
        </div>
      ))}
    </div>
  );
}

function FormulaInputs({ inputs, label }) {
  if (!inputs?.length) {
    return null;
  }

  return (
    <div className="icr-source-values">
      <span className="icr-calc-row__label">{label}</span>
      <div className="icr-calc-inputs">
        {inputs.map((input, index) => (
          <div className="icr-calc-input" key={`${input.label}-${index}`}>
            <span>{input.label}</span>
            <strong>{formatFormulaInput(input)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function RuleAudit({ calculation, summary }) {
  if (!calculation?.rules?.length && !calculation?.selectedRuleIds?.length) {
    return null;
  }

  return (
    <details className="icr-rule-panel" open>
      <summary>{summary}</summary>
      {calculation.rules?.length > 0 ? (
        <div className="icr-rule-list">
          {calculation.rules.map((rule) => (
            <div className="icr-rule" key={rule.ruleId}>
              <div className="icr-rule__heading">
                <code>{rule.ruleId}</code>
                <span
                  className={
                    rule.evidenceSatisfied === false
                      ? "icr-rule__status icr-rule__status--missing"
                      : "icr-rule__status"
                  }
                >
                  {rule.evidenceSatisfied === false
                    ? "Evidence missing"
                    : "Applied"}
                </span>
              </div>
              {rule.excerpt && <p>{rule.excerpt}</p>}
            </div>
          ))}
        </div>
      ) : (
        <p className="icr-calc-row__rules">
          {calculation.selectedRuleIds.join(", ")}
        </p>
      )}
    </details>
  );
}

function CalculationAudit({
  calculation,
  heading,
  formulaLabel,
  inputLabel,
  ruleLabel,
  isBefore = false,
  showEmpty = false,
}) {
  if (!calculation) {
    return showEmpty ? (
      <section className="icr-calculation-audit icr-calculation-audit--empty">
        {heading && <h5>{heading}</h5>}
        <strong>No supported calculation</strong>
        <p>This source did not provide enough evidence for this income type.</p>
      </section>
    ) : null;
  }

  return (
    <section
      className={`icr-calculation-audit${isBefore ? " icr-calculation-audit--before" : ""}`}
    >
      {heading && (
        <div className="icr-calculation-audit__heading">
          <h5>{heading}</h5>
          <StatusPill status={calculation.status} />
        </div>
      )}
      {calculation.formula && (
        <div
          className={`icr-equation${isBefore ? " icr-equation--before" : ""}`}
        >
          <span>{formulaLabel}</span>
          <code>{calculation.formula}</code>
          <strong>= {formatMonthly(calculation.monthly)}</strong>
        </div>
      )}
      <FormulaInputs inputs={calculation.formulaInputs} label={inputLabel} />
      <RuleAudit calculation={calculation} summary={ruleLabel} />
    </section>
  );
}

function CalculationAuditDisplay({ component }) {
  if (component.hasVoeComparison && component.hasIndependentVoeBranch) {
    return (
      <div className="icr-decision-canvas">
        <div className="icr-decision-canvas__heading">
          <div>
            <span>Final method</span>
            <strong>{methodLabel(component.selectedMethod)}</strong>
          </div>
          <StatusPill status={component.status} />
        </div>
        <div
          className="icr-calculation-comparison icr-calculation-comparison--three"
          aria-label="Independent income branches and final calculation"
        >
          <CalculationAudit
            calculation={component.baseline}
            heading="1. W-2 / paystub"
            formulaLabel="W-2 / paystub formula"
            inputLabel="Document values used"
            ruleLabel="Rules considered for this branch"
            isBefore
            showEmpty
          />
          <CalculationAudit
            calculation={component.voeOnly}
            heading="2. VOE only"
            formulaLabel="VOE-only formula"
            inputLabel="Employer-reported values used"
            ruleLabel="Rules considered for this branch"
            isBefore
            showEmpty
          />
          <CalculationAudit
            calculation={component.final}
            heading="3. Final guideline decision"
            formulaLabel="Final selected formula"
            inputLabel="Values used in the final calculation"
            ruleLabel="Rules supporting the final decision"
            showEmpty
          />
        </div>
      </div>
    );
  }
  if (component.hasVoeComparison) {
    return (
      <div
        className="icr-calculation-comparison"
        aria-label="Before and after VOE calculation audit"
      >
        <CalculationAudit
          calculation={component.beforeVoe}
          heading="Before VOE audit (W-2/paystub)"
          formulaLabel="Before VOE formula"
          inputLabel="Inputs used before VOE"
          ruleLabel="Rules applied before VOE"
          isBefore
        />
        <CalculationAudit
          calculation={component}
          heading="Final after VOE audit"
          formulaLabel="Final after VOE formula"
          inputLabel="Inputs used for the final result"
          ruleLabel="Rules applied to the final result"
        />
      </div>
    );
  }

  return (
    <CalculationAudit
      calculation={component}
      formulaLabel="Calculation"
      inputLabel="Values substituted into the formula"
      ruleLabel="Freddie rules considered"
    />
  );
}

function EvidenceTimeline({ rows }) {
  if (!rows?.length) {
    return null;
  }

  return (
    <div className="icr-evidence-timeline">
      <span className="icr-calc-row__label">Evidence timeline</span>
      <p className="icr-evidence-timeline__intro">
        Income periods are kept separate before the final guideline decision.
      </p>
      <div className="icr-evidence-timeline__track">
        {rows.map((row, index) => {
          const amount =
            row.amount ?? row.yearToDateAmount ?? row.currentPeriodAmount;
          const period = [
            row.periodYear,
            row.periodType,
            row.throughDate ? `through ${row.throughDate}` : null,
          ]
            .filter(Boolean)
            .join(" · ");

          return (
            <article
              className="icr-evidence-timeline__item"
              key={`${row.source}-${row.documentSlotId}-${row.earningRowId}-${index}`}
            >
              <span
                className={`icr-source-badge icr-source-badge--${String(
                  row.source || "document",
                ).toLowerCase()}`}
              >
                {row.source || "Document"}
              </span>
              <strong>{period || row.documentSlotId || "Income period"}</strong>
              <span>{formatValue(amount, "money")}</span>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function IncomeCalculation({ income, types = INCOME_TYPES }) {
  return (
    <section className="icr-calc">
      <div className="icr-section-heading">
        <span className="icr-section-icon">
          <CalculateOutlinedIcon fontSize="small" />
        </span>
        <div>
          <h4>How monthly income was calculated</h4>
          <p>Source values, formula, result, and qualification status.</p>
        </div>
      </div>

      <div className="icr-calc__list">
        {types.map((type) => {
          const component = income[type];
          if (!component) {
            return (
              <div className="icr-calc-row icr-calc-row--empty" key={type}>
                <span className="icr-calc-row__type">
                  {incomeTypeLabel(type)}
                </span>
                <span>
                  No {incomeTypeLabel(type).toLowerCase()} income found
                </span>
              </div>
            );
          }

          return (
            <article className="icr-calc-row" key={type}>
              <div className="icr-calc-row__header">
                <div>
                  <span className="icr-calc-row__type">
                    {incomeTypeLabel(type)}
                  </span>
                  <StatusPill status={component.status} />
                  {component.hasIndependentVoeBranch && (
                    <span
                      className={`icr-method-badge icr-method-badge--${component.selectedMethod || "final"}`}
                    >
                      {methodLabel(component.selectedMethod)}
                    </span>
                  )}
                </div>
                <div className="icr-calc-row__amounts">
                  {component.hasIndependentVoeBranch ? (
                    <>
                      <div>
                        <span>W-2 / paystub</span>
                        <strong>
                          {formatMonthly(component.baseline?.monthly)}
                        </strong>
                      </div>
                      <div>
                        <span>VOE only</span>
                        <strong>
                          {formatMonthly(component.voeOnly?.monthly)}
                        </strong>
                      </div>
                      <div className="icr-calc-row__final">
                        <span>Final selected</span>
                        <strong>
                          {formatMonthly(component.final?.monthly)}
                        </strong>
                      </div>
                    </>
                  ) : component.hasVoeComparison ? (
                    <>
                      <div>
                        <span>W-2/paystub before VOE</span>
                        <strong>
                          {formatMonthly(component.beforeVoe?.monthly)}
                        </strong>
                      </div>
                      <div>
                        <span>Final after VOE</span>
                        <strong>{formatMonthly(component.monthly)}</strong>
                      </div>
                      <div className="icr-calc-row__delta">
                        <span>Change</span>
                        <strong>{formatDelta(component.difference)}</strong>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <span>Available from documents</span>
                        <strong>{formatMonthly(component.monthly)}</strong>
                      </div>
                      <div>
                        <span>Currently qualifying</span>
                        <strong>
                          {component.status === "qualifying"
                            ? formatMonthly(component.monthly)
                            : "Not included yet"}
                        </strong>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {component.hasIndependentVoeBranch &&
                component.reconciliationExplanation && (
                  <div className="icr-reconciliation-decision">
                    <div className="icr-reconciliation-decision__selected">
                      <strong>Why this final method was selected</strong>
                      <p>{component.reconciliationExplanation}</p>
                    </div>
                    {component.rejectedAlternative && (
                      <div className="icr-reconciliation-decision__rejected">
                        <strong>Why the alternatives were not selected</strong>
                        <p>{component.rejectedAlternative}</p>
                      </div>
                    )}
                  </div>
                )}

              {!component.hasIndependentVoeBranch &&
                component.reconciliationExplanation && (
                  <div className="icr-voe-change">
                    <strong>Final result after VOE</strong>
                    <p>{component.reconciliationExplanation}</p>
                  </div>
                )}

              {component.changedFields?.length > 0 && (
                <div className="icr-field-changes">
                  <span className="icr-calc-row__label">
                    Fields changed after VOE
                  </span>
                  {component.changedFields.map((change, index) => (
                    <article
                      className="icr-field-change"
                      key={`${change.field}-${index}`}
                    >
                      <strong>{change.field}</strong>
                      <div className="icr-field-change__values">
                        <div>
                          <span>Before VOE</span>
                          <b>
                            {formatValue(change.oldValue, change.valueType)}
                          </b>
                        </div>
                        <span aria-hidden="true">→</span>
                        <div>
                          <span>After VOE</span>
                          <b>
                            {formatValue(change.newValue, change.valueType)}
                          </b>
                        </div>
                      </div>
                      <p>
                        <b>Source:</b> {change.source}
                      </p>
                      <p>{change.explanation}</p>
                    </article>
                  ))}
                </div>
              )}

              {component.voeFieldsUsed?.length > 0 && (
                <div className="icr-source-values">
                  <span className="icr-calc-row__label">
                    VOE fields that changed or confirmed this calculation
                  </span>
                  <div className="icr-calc-inputs">
                    {component.voeFieldsUsed.map((field) => (
                      <div className="icr-calc-input" key={field.fieldKey}>
                        <span>{field.fieldName}</span>
                        <strong>
                          {formatValue(field.value, field.valueType || "money")}
                        </strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {component.resolvedConditions?.length > 0 && (
                <div className="icr-resolved-conditions">
                  <span className="icr-calc-row__label">
                    Conditions answered by VOE
                  </span>
                  {component.resolvedConditions.map((condition, index) => (
                    <div
                      className="icr-condition icr-condition--satisfied"
                      key={`${condition.key || "resolved"}-${index}`}
                    >
                      <StatusPill status="resolved" />
                      <strong>{condition.reason || condition.key}</strong>
                    </div>
                  ))}
                </div>
              )}

              {component.remainingConditions?.length > 0 && (
                <div className="icr-remaining-conditions">
                  <span className="icr-calc-row__label">
                    Conditions still affecting qualification after VOE
                  </span>
                  <p className="icr-remaining-conditions__intro">
                    The VOE did not answer these requirements. This income
                    remains conditional or excluded until they are resolved.
                  </p>
                  {component.remainingConditions.map((condition, index) => (
                    <div
                      className="icr-condition icr-condition--remaining"
                      key={`${condition.key || "remaining"}-${index}`}
                    >
                      <StatusPill status="missing-evidence" />
                      <div>
                        <strong>{condition.reason || condition.key}</strong>
                        <p>Still affects qualification</p>
                        {condition.requestedDocument && (
                          <p>Needed: {condition.requestedDocument}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {component.explanation && (
                <div className="icr-method-explanation">
                  <strong>Why this method was used</strong>
                  <p>{component.explanation}</p>
                </div>
              )}

              <EvidenceTimeline rows={component.evidenceTimeline} />

              <CalculationAuditDisplay component={component} />

              {component.evidence?.length > 0 && (
                <div className="icr-component-evidence">
                  <span className="icr-calc-row__label">
                    Source document evidence
                  </span>
                  {component.evidence.map((evidence, index) => (
                    <div
                      className="icr-component-evidence__item"
                      key={`${evidence.documentId || "evidence"}-${index}`}
                    >
                      <strong>
                        {evidence.documentName ||
                          evidence.documentSlotId ||
                          "Linked document"}
                      </strong>
                      {evidence.quote && <q>{evidence.quote}</q>}
                    </div>
                  ))}
                </div>
              )}

              {!component.hasVoeComparison &&
                component.conditions?.length > 0 && (
                  <div className="icr-condition-list">
                    <span className="icr-calc-row__label">
                      Qualification checks
                    </span>
                    {component.conditions.map((condition, index) => (
                      <div
                        className={`icr-condition icr-condition--${condition.status || "pending"}`}
                        key={`${condition.key || "condition"}-${index}`}
                      >
                        <StatusPill
                          status={
                            condition.status === "satisfied"
                              ? "resolved"
                              : "missing-evidence"
                          }
                        />
                        <div>
                          <strong>{condition.reason}</strong>
                          {condition.requestedDocument && (
                            <p>Needed: {condition.requestedDocument}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              {component.reason &&
                !component.conditions?.length &&
                !component.remainingConditions?.length && (
                  <div className="icr-calc-row__notice">
                    <strong>Why this needs attention:</strong>{" "}
                    {component.reason}
                  </div>
                )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function W2Document({ document, index }) {
  const documentTitle = document.taxYear
    ? `W-2 · ${document.taxYear}`
    : `W-2 · Document ${index + 1}`;

  return (
    <article className="icr-document">
      <header className="icr-document__header">
        <span className="icr-document__icon">
          <DescriptionOutlinedIcon fontSize="small" />
        </span>
        <div>
          <h5>{documentTitle}</h5>
          <p>{document.documentName || "No document linked"}</p>
        </div>
        <StatusPill status={document.status} />
      </header>
      <FieldRows fields={document.fields} />
    </article>
  );
}

function PaystubDocument({ document, index }) {
  const period =
    document.startDate && document.endDate
      ? `${document.startDate} to ${document.endDate}`
      : null;

  return (
    <article className="icr-document">
      <header className="icr-document__header">
        <span className="icr-document__icon icr-document__icon--paystub">
          <ReceiptLongOutlinedIcon fontSize="small" />
        </span>
        <div>
          <h5>
            Paystub {index + 1}
            {document.payDate ? ` · Paid ${document.payDate}` : ""}
          </h5>
          <p>
            {[period, document.payFrequency, document.payType]
              .filter(Boolean)
              .join(" · ") ||
              document.documentName ||
              "No document linked"}
          </p>
        </div>
        <StatusPill status={document.status} />
      </header>

      <div className="icr-document__group">
        <h6>Paystub details</h6>
        <FieldRows fields={document.fields} />
      </div>

      <div className="icr-document__group">
        <h6>Earnings on this paystub</h6>
        {document.earnings?.length > 0 ? (
          <div className="icr-earnings-table">
            <div className="icr-earnings-table__head">
              <span>Description</span>
              <span>Income type</span>
              <span>Current period</span>
              <span>Year to date</span>
            </div>
            {document.earnings.map((earning) => (
              <div className="icr-earnings-row" key={earning.earningRowId}>
                <div>
                  <strong>{earning.description}</strong>
                  {earning.fields.find(
                    (field) => field.fieldId === "incomeType",
                  )?.explanation && (
                    <details className="icr-field-row__audit">
                      <summary>Why this income type?</summary>
                      <p>
                        {
                          earning.fields.find(
                            (field) => field.fieldId === "incomeType",
                          ).explanation
                        }
                      </p>
                    </details>
                  )}
                </div>
                <span>{earning.incomeType || "Needs review"}</span>
                <strong>
                  {formatValue(earning.currentPeriodAmount, "money")}
                </strong>
                <strong>
                  {formatValue(earning.yearToDateAmount, "money")}
                </strong>
              </div>
            ))}
          </div>
        ) : (
          <div className="icr-doc__empty">No earnings rows were extracted.</div>
        )}
      </div>
    </article>
  );
}

function VoeDocument({ document, index }) {
  return (
    <article className="icr-document icr-document--voe">
      <header className="icr-document__header">
        <span className="icr-document__icon">
          <WorkOutlineIcon fontSize="small" />
        </span>
        <div>
          <h5>
            Verification of Employment {index + 1}
            {document.statementDate ? ` · ${document.statementDate}` : ""}
          </h5>
          <p>{document.documentName || "Employer verification"}</p>
        </div>
        <StatusPill status={document.status} />
      </header>

      <div className="icr-document__group">
        <h6>Current employment and pay</h6>
        <FieldRows fields={document.fields} />
      </div>

      <div className="icr-document__group">
        <h6>Employer-reported earnings history</h6>
        {document.earnings?.length > 0 ? (
          <div className="icr-earnings-table icr-earnings-table--voe">
            <div className="icr-earnings-table__head">
              <span>Description</span>
              <span>Income type</span>
              <span>Year / period</span>
              <span>Amount</span>
            </div>
            {document.earnings.map((earning) => (
              <div className="icr-earnings-row" key={earning.earningRowId}>
                <strong>{earning.description}</strong>
                <span>{earning.incomeType || "Needs review"}</span>
                <span>
                  {[earning.periodYear, earning.periodType]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </span>
                <strong>{formatValue(earning.amount, "money")}</strong>
              </div>
            ))}
          </div>
        ) : (
          <div className="icr-doc__empty">
            This VOE did not contain earnings amounts.
          </div>
        )}
      </div>
    </article>
  );
}

function DocumentsSection({ documents }) {
  const w2s = documents?.w2s || [];
  const paystubs = documents?.paystubs || [];
  const voes = documents?.voes || [];

  return (
    <section className="icr-documents">
      <div className="icr-section-heading">
        <span className="icr-section-icon">
          <DescriptionOutlinedIcon fontSize="small" />
        </span>
        <div>
          <h4>Source documents and extracted fields</h4>
          <p>
            W-2, paystub, and VOE values stay separated so every correction is
            traceable.
          </p>
        </div>
      </div>

      <div className="icr-document-family">
        <div className="icr-document-family__title">
          <h5>W-2 history</h5>
          <span>
            {w2s.length} document{w2s.length === 1 ? "" : "s"}
          </span>
        </div>
        {w2s.length > 0 ? (
          w2s.map((document, index) => (
            <W2Document
              document={document}
              index={index}
              key={document.slotId}
            />
          ))
        ) : (
          <div className="icr-doc__empty">No W-2 documents were linked.</div>
        )}
      </div>

      <div className="icr-document-family">
        <div className="icr-document-family__title">
          <h5>Paystub history</h5>
          <span>
            {paystubs.length} document{paystubs.length === 1 ? "" : "s"}
          </span>
        </div>
        {paystubs.length > 0 ? (
          paystubs.map((document, index) => (
            <PaystubDocument
              document={document}
              index={index}
              key={document.slotId}
            />
          ))
        ) : (
          <div className="icr-doc__empty">
            No paystub documents were linked.
          </div>
        )}
      </div>

      <div className="icr-document-family">
        <div className="icr-document-family__title">
          <h5>Verification of Employment</h5>
          <span>
            {voes.length} document{voes.length === 1 ? "" : "s"}
          </span>
        </div>
        {voes.length > 0 ? (
          voes.map((document, index) => (
            <VoeDocument
              document={document}
              index={index}
              key={document.slotId}
            />
          ))
        ) : (
          <div className="icr-doc__empty">
            No VOE was linked to this employment.
          </div>
        )}
      </div>
    </section>
  );
}

function componentExpression(terms) {
  if (!terms?.length) {
    return "No income components";
  }

  return terms
    .map(
      (component) => `${component.incomeType} ${fmtMoney(component.monthly)}`,
    )
    .join(" + ");
}

/** Employments and businesses roll up identically; only the label differs. */
function rollupSources(borrower) {
  return [
    ...borrower.employments.map((employment) => ({
      id: employment.employmentId,
      name: employment.employerName,
      totals: employment.totals,
    })),
    ...(borrower.businesses || []).map((business) => ({
      id: business.businessId,
      name: business.businessName,
      totals: business.totals,
    })),
  ];
}

function RollupMode({ tree, mode }) {
  const isQualifying = mode === "qualifying";
  const label = isQualifying ? "Qualifying" : "Available";
  const loanTotal = tree.loanTotals?.[mode];

  return (
    <div className={`icr-rollup icr-rollup--${mode}`}>
      <header className="icr-rollup__header">
        <div>
          <span>{label} monthly income</span>
          <p>
            {isQualifying
              ? "Only components with every required condition satisfied."
              : "All income calculated from the documents currently available."}
          </p>
        </div>
        <strong>{formatMonthly(loanTotal)}</strong>
      </header>

      <div className="icr-rollup__borrowers">
        {tree.borrowers.map((borrower, borrowerIndex) => {
          const borrowerTotal = isQualifying
            ? borrower.qualifyingTotal
            : borrower.availableTotal;

          return (
            <div className="icr-rollup-borrower" key={borrower.borrowerId}>
              <div className="icr-rollup-borrower__heading">
                <strong>
                  Borrower {borrowerIndex + 1}:{" "}
                  {borrower.borrowerName || borrower.borrowerId}
                </strong>
                <span>{formatMonthly(borrowerTotal)}</span>
              </div>

              {rollupSources(borrower).map((source) => {
                const terms = isQualifying
                  ? source.totals.qualifyingTerms
                  : source.totals.availableTerms;
                const sourceTotal = isQualifying
                  ? source.totals.qualifyingTotal
                  : source.totals.availableTotal;
                const pendingTerms = isQualifying
                  ? source.totals.availableTerms.filter(
                      (component) => component.status !== "qualifying",
                    )
                  : [];

                return (
                  <div className="icr-rollup-employment" key={source.id}>
                    <span>{source.name}</span>
                    <div>
                      <code>{componentExpression(terms)}</code>
                      <strong>= {fmtMoney(sourceTotal)}</strong>
                    </div>
                    {pendingTerms.length > 0 && (
                      <p>
                        Not included yet: {componentExpression(pendingTerms)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="icr-rollup__loan-equation">
        <span>Loan formula</span>
        <code>
          {tree.borrowers
            .map((borrower, index) => {
              const total = isQualifying
                ? borrower.qualifyingTotal
                : borrower.availableTotal;

              return `Borrower ${index + 1} (${typeof total === "number" ? fmtMoney(total) : "pending"})`;
            })
            .join(" + ")}
        </code>
        <strong>
          ={" "}
          {typeof loanTotal === "number"
            ? fmtMoney(loanTotal)
            : "Pending unresolved borrower or employment review"}
        </strong>
      </div>
    </div>
  );
}

function VoeCorrectionRollup({ tree }) {
  if (!tree.hasVoeReconciliation) {
    return null;
  }
  if (!tree.hasIndependentVoeReconciliation) {
    return (
      <div className="icr-voe-rollup">
        <header>
          <div>
            <span>VOE correction summary</span>
            <p>What changed from the W-2/paystub calculation.</p>
          </div>
          <StatusPill status="resolved" />
        </header>
        <div className="icr-voe-rollup__totals">
          <div>
            <span>Before VOE</span>
            <strong>
              {formatMonthly(tree.loanTotals?.baselineQualifying)}
            </strong>
          </div>
          <div>
            <span>Final after VOE</span>
            <strong>
              {formatMonthly(tree.loanTotals?.finalAfterVoeQualifying)}
            </strong>
          </div>
          <div>
            <span>Difference</span>
            <strong>{formatDelta(tree.loanTotals?.difference)}</strong>
          </div>
        </div>
      </div>
    );
  }
  const hasRemainingConditions = tree.borrowers.some((borrower) =>
    borrower.employments.some((employment) =>
      Object.values(employment.income).some(
        (component) => component?.remainingConditions?.length > 0,
      ),
    ),
  );

  return (
    <div className="icr-voe-rollup">
      <header>
        <div>
          <span>Independent calculation summary</span>
          <p>
            W-2/paystub and VOE were calculated separately before the final
            guideline decision.
          </p>
        </div>
        <StatusPill
          status={hasRemainingConditions ? "conditional" : "resolved"}
        />
      </header>
      <div className="icr-voe-rollup__totals">
        <div>
          <span>W-2 / paystub qualifying</span>
          <strong>{formatMonthly(tree.loanTotals?.baselineQualifying)}</strong>
        </div>
        <div>
          <span>VOE-only calculated</span>
          <strong>{formatMonthly(tree.loanTotals?.voeOnlyAvailable)}</strong>
          <small>
            Qualifying alone:{" "}
            {formatMonthly(tree.loanTotals?.voeOnlyQualifying)}
          </small>
        </div>
        <div className="icr-voe-rollup__final">
          <span>Final reconciled qualifying</span>
          <strong>
            {formatMonthly(tree.loanTotals?.finalAfterVoeQualifying)}
          </strong>
        </div>
      </div>
      <div className="icr-voe-rollup__borrowers">
        {tree.borrowers.map((borrower, borrowerIndex) => (
          <div key={borrower.borrowerId}>
            <strong>
              Borrower {borrowerIndex + 1}:{" "}
              {borrower.borrowerName || borrower.borrowerId}
            </strong>
            <span>
              W-2/paystub {formatMonthly(borrower.baselineQualifyingTotal)} ·
              VOE-only {formatMonthly(borrower.voeOnlyAvailableTotal)} · Final{" "}
              {formatMonthly(borrower.qualifyingTotal)}
            </span>
            {borrower.employments.map((employment) => (
              <p key={employment.employmentId}>
                {employment.employerName}: W-2/paystub{" "}
                {formatMonthly(employment.totals.baselineQualifyingTotal)} ·
                VOE-only{" "}
                {formatMonthly(employment.totals.voeOnlyAvailableTotal)} · Final{" "}
                {formatMonthly(employment.totals.qualifyingTotal)}{" "}
              </p>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function LoanTotals({ tree }) {
  return (
    <section className="icr-final">
      <div className="icr-section-heading icr-section-heading--compact">
        <span className="icr-section-icon">
          <CalculateOutlinedIcon fontSize="small" />
        </span>
        <div>
          <h4>How the loan totals were reached</h4>
          <p>
            Actual component arithmetic from each employment, borrower, and the
            full loan.
          </p>
        </div>
      </div>
      <VoeCorrectionRollup tree={tree} />
      <div className="icr-rollups">
        <RollupMode tree={tree} mode="qualifying" />
        <RollupMode tree={tree} mode="available" />
      </div>
    </section>
  );
}

function EmploymentNode({
  employment,
  hasVoeReconciliation,
  hasIndependentVoeReconciliation,
}) {
  return (
    <section className="icr-employment">
      <header className="icr-employment__header">
        <span className="icr-employment__icon">
          <WorkOutlineIcon />
        </span>
        <div className="icr-employment__identity">
          <span>Employment</span>
          <h3>{employment.employerName || employment.employmentId}</h3>
          <p>{employment.status || "Employment status not available"}</p>
        </div>
        <div className="icr-employment__totals">
          {hasIndependentVoeReconciliation ? (
            <>
              <div>
                <span>W-2 / paystub</span>
                <strong>
                  {formatMonthly(employment.totals.baselineQualifyingTotal)}
                </strong>
              </div>
              <div>
                <span>VOE only</span>
                <strong>
                  {formatMonthly(employment.totals.voeOnlyAvailableTotal)}
                </strong>
              </div>
              <div>
                <span>Final</span>
                <strong>
                  {formatMonthly(employment.totals.qualifyingTotal)}
                </strong>
              </div>
            </>
          ) : hasVoeReconciliation ? (
            <>
              <div>
                <span>W-2/paystub before VOE</span>
                <strong>
                  {formatMonthly(employment.totals.baselineQualifyingTotal)}
                </strong>
              </div>
              <div>
                <span>Final after VOE</span>
                <strong>
                  {formatMonthly(employment.totals.qualifyingTotal)}
                </strong>
              </div>
              <div>
                <span>Change</span>
                <strong>
                  {formatDelta(employment.totals.qualifyingDifference)}
                </strong>
              </div>
            </>
          ) : (
            <>
              <div>
                <span>Available</span>
                <strong>{fmtMoney(employment.totals.availableTotal)}</strong>
              </div>
              <div>
                <span>Qualifying</span>
                <strong>{fmtMoney(employment.totals.qualifyingTotal)}</strong>
              </div>
            </>
          )}
        </div>
      </header>

      <div className="icr-employment__body">
        <IncomeCalculation income={employment.income} />

        {employment.fields?.length > 0 && (
          <section className="icr-employment-fields">
            <div className="icr-section-heading icr-section-heading--compact">
              <span className="icr-section-icon">
                <WorkOutlineIcon fontSize="small" />
              </span>
              <div>
                <h4>Employment details</h4>
                <p>Employer-level fields used to establish this employment.</p>
              </div>
            </div>
            <FieldRows fields={employment.fields} />
          </section>
        )}

        <DocumentsSection documents={employment.documents} />
      </div>
    </section>
  );
}

function BusinessDocument({ document, index }) {
  const title = document.taxYear
    ? `Tax year ${document.taxYear}`
    : `Business document ${index + 1}`;

  return (
    <article className="icr-document">
      <header className="icr-document__header">
        <span className="icr-document__icon">
          <DescriptionOutlinedIcon fontSize="small" />
        </span>
        <div>
          <h5>{title}</h5>
          <p>{document.documentName || "No document name recorded"}</p>
        </div>
      </header>
      <FieldRows fields={document.fields} />
    </article>
  );
}

/** Per-tax-year arithmetic behind a business component (Form 91 style). */
function AnnualAnalyses({ analyses }) {
  if (!analyses?.length) {
    return null;
  }

  return (
    <section className="icr-annual-analyses">
      <div className="icr-section-heading icr-section-heading--compact">
        <span className="icr-section-icon">
          <CalculateOutlinedIcon fontSize="small" />
        </span>
        <div>
          <h4>Year-by-year business income</h4>
          <p>Adjusted annual income derived from each tax year.</p>
        </div>
      </div>
      {analyses.map((analysis) => (
        <article
          className="icr-annual-analysis"
          key={analysis.taxYear || analysis.formula}
        >
          <div className="icr-annual-analysis__header">
            <strong>{analysis.taxYear || "Tax year not stated"}</strong>
            <strong>{formatValue(analysis.adjustedAnnualIncome, "money")}</strong>
          </div>
          {analysis.formula && (
            <div className="icr-income__formula">{analysis.formula}</div>
          )}
          {analysis.adjustments && (
            <div className="icr-calc-inputs">
              {Object.entries(analysis.adjustments).map(([label, value]) => (
                <div className="icr-calc-input" key={label}>
                  <span>{label.replace(/([a-z])([A-Z])/g, "$1 $2")}</span>
                  <strong>{formatValue(value, "money")}</strong>
                </div>
              ))}
            </div>
          )}
        </article>
      ))}
    </section>
  );
}

/**
 * Self-employment sibling of EmploymentNode. Business components hang off a
 * businessId rather than an employmentId, so they need their own branch;
 * everything below the header reuses the wage renderers.
 */
function BusinessNode({ business }) {
  const analyses = business.incomeTypes
    .map((type) => business.income[type]?.annualAnalyses || [])
    .flat();

  return (
    <section className="icr-employment icr-employment--business">
      <header className="icr-employment__header">
        <span className="icr-employment__icon">
          <BusinessCenterOutlinedIcon />
        </span>
        <div className="icr-employment__identity">
          <span>Business</span>
          <h3>{business.businessName}</h3>
          <p>{calculationTrackLabel(business.calculationTrack)}</p>
        </div>
        <div className="icr-employment__totals">
          <div>
            <span>Available</span>
            <strong>{fmtMoney(business.totals.availableTotal)}</strong>
          </div>
          <div>
            <span>Qualifying</span>
            <strong>{fmtMoney(business.totals.qualifyingTotal)}</strong>
          </div>
        </div>
      </header>

      <div className="icr-employment__body">
        <IncomeCalculation
          income={business.income}
          types={business.incomeTypes}
        />

        <AnnualAnalyses analyses={analyses} />

        {business.fields?.length > 0 && (
          <section className="icr-employment-fields">
            <div className="icr-section-heading icr-section-heading--compact">
              <span className="icr-section-icon">
                <BusinessCenterOutlinedIcon fontSize="small" />
              </span>
              <div>
                <h4>Business details</h4>
                <p>Business-level fields used to establish this income.</p>
              </div>
            </div>
            <FieldRows fields={business.fields} />
          </section>
        )}

        {business.documents?.length > 0 && (
          <section className="icr-documents">
            <div className="icr-section-heading">
              <span className="icr-section-icon">
                <DescriptionOutlinedIcon fontSize="small" />
              </span>
              <div>
                <h4>Source documents and extracted fields</h4>
                <p>Every value read off the business tax documents.</p>
              </div>
            </div>
            <div className="icr-document-family">
              <div className="icr-document-family__title">
                <h5>Business tax documents</h5>
                <span>
                  {business.documents.length} document
                  {business.documents.length === 1 ? "" : "s"}
                </span>
              </div>
              {business.documents.map((document, index) => (
                <BusinessDocument
                  document={document}
                  index={index}
                  key={document.documentId || document.taxYear || index}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </section>
  );
}

function BorrowerNode({
  borrower,
  index,
  hasVoeReconciliation,
  hasIndependentVoeReconciliation,
}) {
  return (
    <section className="icr-borrower">
      <header className="icr-borrower__header">
        <span className="icr-borrower__icon">
          <PersonOutlineIcon />
        </span>
        <div>
          <span>Borrower {index + 1}</span>
          <h2>{borrower.borrowerName || borrower.borrowerId}</h2>
        </div>
        <div className="icr-borrower__totals">
          {hasIndependentVoeReconciliation ? (
            <>
              <div>
                <span>W-2 / paystub</span>
                <strong>
                  {formatMonthly(borrower.baselineQualifyingTotal)}
                </strong>
              </div>
              <div>
                <span>VOE only</span>
                <strong>{formatMonthly(borrower.voeOnlyAvailableTotal)}</strong>
              </div>
              <div>
                <span>Final qualifying</span>
                <strong>{formatMonthly(borrower.qualifyingTotal)}</strong>
              </div>
            </>
          ) : hasVoeReconciliation ? (
            <>
              <div>
                <span>W-2/paystub before VOE</span>
                <strong>
                  {typeof borrower.baselineQualifyingTotal === "number"
                    ? `${fmtMoney(borrower.baselineQualifyingTotal)} / mo`
                    : "Not available"}
                </strong>
              </div>
              <div>
                <span>Final after VOE</span>
                <strong>
                  {typeof borrower.qualifyingTotal === "number"
                    ? `${fmtMoney(borrower.qualifyingTotal)} / mo`
                    : "Not available"}
                </strong>
              </div>
              <div>
                <span>Change</span>
                <strong>
                  {formatDelta(
                    typeof borrower.baselineQualifyingTotal === "number" &&
                      typeof borrower.qualifyingTotal === "number"
                      ? borrower.qualifyingTotal -
                          borrower.baselineQualifyingTotal
                      : null,
                  )}
                </strong>
              </div>
            </>
          ) : (
            <>
              <div>
                <span>Available income</span>
                <strong>
                  {typeof borrower.availableTotal === "number"
                    ? `${fmtMoney(borrower.availableTotal)} / mo`
                    : "Not available"}
                </strong>
              </div>
              <div>
                <span>Qualifying income</span>
                <strong>
                  {typeof borrower.qualifyingTotal === "number"
                    ? `${fmtMoney(borrower.qualifyingTotal)} / mo`
                    : "Not available"}
                </strong>
              </div>
            </>
          )}
        </div>
      </header>

      <div className="icr-borrower__body">
        {borrower.employments.map((employment) => (
          <EmploymentNode
            employment={employment}
            key={employment.employmentId}
            hasVoeReconciliation={hasVoeReconciliation}
            hasIndependentVoeReconciliation={hasIndependentVoeReconciliation}
          />
        ))}
        {(borrower.businesses || []).map((business) => (
          <BusinessNode business={business} key={business.businessId} />
        ))}
        {borrower.employments.length === 0 &&
          (borrower.businesses || []).length === 0 && (
            <div className="icr-doc__empty">
              No employment or business income was resolved for this borrower.
            </div>
          )}
      </div>
    </section>
  );
}

export default function IncomeCalculationRunDetailPage({ onReady }) {
  const [theme] = useDarkMode();
  const { jobId } = useParams();
  const history = useHistory();
  const queryEnv = useQueryEnv();
  const env = queryEnv || localStorage.getItem("icr-env") || "dev";

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchIncomeRunDetail(jobId, env);
      if (res.success) {
        setDetail(res);
      } else {
        setError(res.reason || "Failed to load run.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      onReady?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, env]);

  useEffect(() => {
    load();
  }, [load]);

  const tree = useMemo(() => {
    if (!detail) {
      return null;
    }
    return buildIncomeTree(detail.artifacts);
  }, [detail]);

  const job = detail?.job;
  const result = detail?.artifacts?.result;

  return (
    <div className="icr-page" data-theme={theme}>
      <button
        className="icr-back"
        onClick={() => history.push(`/income-calculation-runs?env=${env}`)}
      >
        ← Back to runs
      </button>

      {loading && <div className="icr-muted">Loading run…</div>}
      {error && <div className="icr-error">{error}</div>}

      {!loading && job && (
        <>
          <div className="icr-header">
            <div>
              <span className="icr-header__context">
                Income calculation review
              </span>
              <h1 className="icr-title">Loan {job.loanId}</h1>
              <p className="icr-subtitle">
                <span className={`icr-envbadge icr-envbadge--${env}`}>
                  {env === "prod" ? "PROD DB" : "DEV DB"}
                </span>{" "}
                Organization {job.organizationId}
              </p>
            </div>
            <StatusPill status={job.status} />
          </div>

          <section
            className={`icr-summary ${
              tree?.hasIndependentVoeReconciliation
                ? "icr-summary--three-stage"
                : ""
            }`}
          >
            {tree?.hasIndependentVoeReconciliation ? (
              <>
                <div className="icr-summary__primary">
                  <span>W-2 / paystub qualifying</span>
                  <strong>
                    {formatMonthly(tree.loanTotals?.baselineQualifying)}
                  </strong>
                  <p>Calculated without using VOE earnings.</p>
                </div>
                <div className="icr-summary__primary icr-summary__primary--voe">
                  <span>VOE-only calculated</span>
                  <strong>
                    {formatMonthly(tree.loanTotals?.voeOnlyAvailable)}
                  </strong>
                  <p>
                    Qualifying from VOE alone:{" "}
                    {formatMonthly(tree.loanTotals?.voeOnlyQualifying)}
                  </p>
                </div>
                <div className="icr-summary__primary icr-summary__primary--qualifying">
                  <span>Final reconciled qualifying</span>
                  <strong>
                    {formatMonthly(tree.loanTotals?.finalAfterVoeQualifying)}
                  </strong>
                  <p>The guideline-selected result from both analyses.</p>
                </div>
              </>
            ) : tree?.hasVoeReconciliation ? (
              <>
                <div className="icr-summary__primary">
                  <span>W-2/paystub qualifying before VOE</span>
                  <strong>
                    {formatMonthly(tree.loanTotals?.baselineQualifying)}
                  </strong>
                  <p>The original calculation and conditions before VOE.</p>
                </div>
                <div className="icr-summary__primary icr-summary__primary--qualifying">
                  <span>Final qualifying after VOE</span>
                  <strong>
                    {formatMonthly(tree.loanTotals?.finalAfterVoeQualifying)}
                  </strong>
                  <p>
                    The corrected result after employer evidence was applied.
                  </p>
                </div>
                <div className="icr-summary__meta">
                  <span>VOE difference</span>
                  <strong>{formatDelta(tree.loanTotals?.difference)}</strong>
                </div>
              </>
            ) : (
              <>
                <div className="icr-summary__primary">
                  <span>Available monthly income</span>
                  <strong>
                    {typeof result?.availableMonthlyIncomeTotal === "number"
                      ? fmtMoney(result.availableMonthlyIncomeTotal)
                      : "Not available"}
                  </strong>
                  <p>
                    Income calculated from the documents currently available.
                  </p>
                </div>
                <div className="icr-summary__primary icr-summary__primary--qualifying">
                  <span>Qualifying monthly income</span>
                  <strong>
                    {typeof result?.qualifyingMonthlyIncomeTotal === "number"
                      ? fmtMoney(result.qualifyingMonthlyIncomeTotal)
                      : "Not available"}
                  </strong>
                  <p>
                    Income that currently meets the documented requirements.
                  </p>
                </div>
              </>
            )}
            {!tree?.hasIndependentVoeReconciliation && (
              <>
                <div className="icr-summary__meta">
                  <span>Borrowers</span>
                  <strong>{tree ? tree.borrowers.length : "—"}</strong>
                </div>
                <div className="icr-summary__meta">
                  <span>Run result</span>
                  <strong>
                    {STATUS_COPY[result?.status] ||
                      result?.status ||
                      job.status}
                  </strong>
                </div>
              </>
            )}
          </section>

          {!detail.artifacts.hasArtifacts && (
            <div className="icr-error">
              This run has no artifacts to display yet
              {job.failureReason ? ` — ${job.failureReason}` : ""}.
            </div>
          )}

          <div className="icr-detail-grid">
            <div>
              {tree && tree.borrowers.length > 0 && (
                <div className="icr-tree">
                  {tree.borrowers.map((b, index) => (
                    <BorrowerNode
                      key={b.borrowerId}
                      borrower={b}
                      index={index}
                      hasVoeReconciliation={tree.hasVoeReconciliation}
                      hasIndependentVoeReconciliation={
                        tree.hasIndependentVoeReconciliation
                      }
                    />
                  ))}
                </div>
              )}

              {tree && tree.borrowers.length > 0 && <LoanTotals tree={tree} />}

              {detail.artifacts.hasArtifacts && (
                <div className="icr-advanced">
                  <button
                    className="icr-collapse-toggle"
                    onClick={() => setShowRaw((v) => !v)}
                  >
                    {showRaw ? "Hide" : "Show"} raw artifacts (advanced)
                  </button>
                  {showRaw && (
                    <pre>{JSON.stringify(detail.artifacts, null, 2)}</pre>
                  )}
                </div>
              )}
            </div>

            <ChatPanel jobId={jobId} env={env} loanId={job.loanId} />
          </div>
        </>
      )}
    </div>
  );
}
